import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import type { ListVariantTypeAndProps } from '@aurora/shared-client/components/common/List';
import {
  PagerLoadMoreVariant,
  PagerPosition,
  PagerVariant
} from '@aurora/shared-client/components/common/Pager/enums';
import type { PagerVariantTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import type { SearchResultsPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { LoadingSize } from '@aurora/shared-client/types/enums';
import type {
  MessageSearchConnection,
  MessageSearchConstraints,
  MessageSearchSorts
} from '@aurora/shared-generated/types/graphql-schema-types';
import { SortDirection } from '@aurora/shared-generated/types/graphql-schema-types';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import { getAsEnum } from '@aurora/shared-utils/helpers/objects/EnumHelper';
import React, { useContext } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import { ItemType, MessageTimestamp, MessageViewVariant, ViewSize } from '../../../types/enums';
import type {
  ContextNodeFragment,
  ContextUserFragment,
  MessageSearchQuery,
  MessageSearchQueryVariables,
  MessageSearchViewFragment
} from '../../../types/graphql-types';
import PaneledItemList from '../../common/List/PaneledItemList/PaneledItemList';
import type { ItemViewTypeAndProps, ItemViewVariantProps } from '../../entities/types';
import { LastUpdateFilterMenuItem, MessageSortMenuItem } from '../../search/enums';
import { getSearchFilterParams, NodeTypeToIdMap } from '../../search/SearchHelper';
import type { MessageSearchPageConstraintsAndSorts } from '../../search/types';
import useEntityViewQuery from '../../useEntityViewQuery';
import useTranslation from '../../useTranslation';
import localStyles from './MessageListForSearch.module.pcss';
import messageSearchQuery from './MessageSearch.query.graphql';
import MessageSearchContext from './MessageSearchContext';

interface Props {
  /**
   * Callback that triggers when useQuery is success.
   *
   * @callback
   * @param results from useQuery graphQl call
   */
  onCompleted?: (searchResults: MessageSearchQuery) => void;
  /**
   * Property to pass the term to query.
   */
  queryTerm?: string;
  /**
   * Node which search should be scoped to. When not provided, search is scoped to community.
   */
  contextNode?: ContextNodeFragment;
  /**
   * The author(user) data, selected from author filter
   */
  author?: ContextUserFragment;
  /**
   * Whether to use header for the PaneledItemList.
   */
  useHeader?: boolean;
  /**
   * The list variant type and props.
   */
  listVariant: ListVariantTypeAndProps<MessageSearchViewFragment>;
  /**
   * Message view variant props overrides.
   */
  messageViewVariantPropsOverrides?: ItemViewVariantProps<
    ItemType.MESSAGE_SEARCH,
    MessageViewVariant.INLINE
  >;
  /**
   * Pager variant type and props.
   */
  pagerVariant?: PagerVariantTypeAndProps<EndUserPages, EndUserQueryParams>;
  /**
   * The initial page size.
   */
  pageSize?: number;
  /**
   * Number of additional results to return on loading more
   */
  loadMorePageSize?: number;
  /**
   * Classname for the paneled item list component.
   */
  paneledListClassname?: string;
  /**
   * Classname for the panel header section.
   */
  headerClassname?: string;
  /**
   * Classname for the panel footer section.
   */
  footerClassname?: string;
  /**
   * Applies panel styling to the component.
   */
  panel?: PanelType;
  /**
   * Whether to display empty state or not.
   */
  useEmpty?: boolean;
  /**
   * Whether to use additional constraints and sorts from query parameters or not.
   */
  useAdditionalConstraintsAndSorts?: boolean;
  /**
   * Whether to display a loader or not
   */
  useLoader?: boolean;
}

export const MessageSortMenuItemMap: Record<MessageSortMenuItem, MessageSearchSorts> = {
  [MessageSortMenuItem.NEWEST]: { topicPublishDate: { direction: SortDirection.Desc } },
  [MessageSortMenuItem.MOST_LIKED]: { topicKudosCount: { direction: SortDirection.Desc } },
  [MessageSortMenuItem.MOST_VIEWED]: { topicViews: { direction: SortDirection.Desc } },
  [MessageSortMenuItem.MOST_RELEVANT]: { score: { direction: SortDirection.Desc } }
};

/**
 * Displays a list of messages for a given search term - used in search page
 * using messageSearch query
 *
 *  @author Amit Agrawal
 */
const MessageListForSearch: React.FC<React.PropsWithChildren<Props>> = ({
  onCompleted,
  queryTerm,
  contextNode,
  author,
  useHeader = true,
  listVariant,
  messageViewVariantPropsOverrides = {},
  pagerVariant = {
    type: PagerVariant.LOAD_MORE,
    props: {
      position: PagerPosition.START,
      variant: PagerLoadMoreVariant.BOXED
    }
  },
  pageSize = 5,
  loadMorePageSize,
  paneledListClassname,
  headerClassname,
  footerClassname,
  panel = PanelType.NONE,
  useEmpty = false,
  useLoader = false,
  useAdditionalConstraintsAndSorts = false
}: Props) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.MESSAGE_LIST_FOR_SEARCH
  );
  const { FormattedMessage } = useTranslation(EndUserComponent.MESSAGE_LIST_FOR_SEARCH);
  const { router } = useEndUserRoutes();
  const { authUser } = useContext(AppContext);
  const sortByStringFromUrl = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_SORT_BY);
  const sortByValue = getAsEnum(
    sortByStringFromUrl,
    MessageSortMenuItem,
    MessageSortMenuItem.MOST_RELEVANT
  );

  const lastUpdateFilterFromUrl = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_LAST_UPDATE,
    ''
  );
  const lastUpdateFilterValue = getAsEnum(lastUpdateFilterFromUrl, LastUpdateFilterMenuItem);

  const trimmedTerm: string = queryTerm?.trim() ?? '';

  const messageViewVariantDefaultProps: ItemViewVariantProps<
    ItemType.MESSAGE,
    MessageViewVariant.INLINE
  > = {
    boardIconSize: IconSize.PX_30,
    viewSize: ViewSize.SM,
    subjectAs: 'h6',
    clampBodyLines: 1,
    useTags: false,
    useAttachments: true,
    timeStampType:
      lastUpdateFilterValue || sortByValue === MessageSortMenuItem.NEWEST
        ? MessageTimestamp.LAST_PUBLISHED_TIME
        : MessageTimestamp.POST_TIME
  };

  const messageViewVariant: ItemViewTypeAndProps<ItemType.MESSAGE_SEARCH, MessageViewVariant> = {
    type: MessageViewVariant.INLINE,
    props: {
      ...messageViewVariantDefaultProps,
      ...messageViewVariantPropsOverrides
    }
  };

  const searchEventTrackingIdMap = {};

  const messageSortItems = [
    MessageSortMenuItem.MOST_RELEVANT,
    MessageSortMenuItem.NEWEST,
    MessageSortMenuItem.MOST_LIKED,
    MessageSortMenuItem.MOST_VIEWED
  ];

  const messageConstraints: MessageSearchConstraints = {};

  if (contextNode && contextNode.nodeType !== NodeType.COMMUNITY) {
    messageConstraints[NodeTypeToIdMap[contextNode.nodeType]] = {
      eq: contextNode.id
    };
  }

  let messageViewQueryVariables: MessageSearchQueryVariables = {
    constraints: messageConstraints,
    first: pageSize,
    searchTerm: trimmedTerm
  };

  /**
   * Function to get the filter and sort value from query parameter
   */
  const getAdditionalFilterAndSorts = () => {
    let constraintsAndSorts: MessageSearchPageConstraintsAndSorts = {};
    const searchFilterConstraints = getSearchFilterParams(router.query, authUser, author);
    if (sortByValue) {
      constraintsAndSorts = {
        ...constraintsAndSorts,
        sorts: {
          ...MessageSortMenuItemMap[sortByValue]
        }
      };
    }
    if (searchFilterConstraints) {
      constraintsAndSorts = {
        ...constraintsAndSorts,
        constraints: {
          ...constraintsAndSorts.constraints,
          ...searchFilterConstraints
        }
      };
    }
    return constraintsAndSorts;
  };

  /**
   * Update the message query variables with additional constraints and sorts
   */
  if (useAdditionalConstraintsAndSorts) {
    const additionalConstraintsAndSorts = getAdditionalFilterAndSorts();
    const { constraints, sorts } = additionalConstraintsAndSorts;
    messageViewQueryVariables = {
      ...messageViewQueryVariables,
      constraints: {
        ...messageViewQueryVariables.constraints,
        ...constraints
      },
      sorts
    };
  }

  const queryResult = useEntityViewQuery<
    ItemType.MESSAGE_SEARCH,
    MessageSearchQuery,
    MessageSearchQueryVariables
  >(module, ItemType.MESSAGE_SEARCH, messageViewVariant, messageSearchQuery, {
    variables: messageViewQueryVariables,
    onCompleted,
    onError: React.useCallback(() => {
      onCompleted(null);
    }, [onCompleted]),
    notifyOnNetworkStatusChange: true,
    skip: trimmedTerm.length < 2
  });

  const { data, loading } = queryResult;

  if (!loading && data) {
    const { searchEventTrackingId } = data.messageSearch;
    const messageIds = data.messageSearch.results.edges.map(option => option.node.message.id);
    if (messageIds) {
      messageIds.forEach(messageId => {
        searchEventTrackingIdMap[messageId] = searchEventTrackingId;
      });
    }
  }

  if (textLoading) {
    return null;
  }

  /**
   * The no result section to be rendered for the PaneledItemList.
   */
  const NoResults: React.FC<React.PropsWithChildren<unknown>> = () => (
    <EmptyState
      className={cx('lia-g-pt-0')}
      description={formatMessage('emptyDescription')}
      descriptionClassName={cx('lia-empty-state-description')}
      alignment={TextAlignment.LEFT}
    />
  );

  /**
   * If there is an error while fetching data, display empty state
   *
   * @todo Once we have addressed all possible filter combinations,
   * and only then make the call, we may want to remove this
   *
   */
  if (queryResult?.error) {
    return <NoResults />;
  }

  const totalResultCount =
    (queryResult.data?.messageSearch?.results?.edges.length > 0 &&
      queryResult.data?.messageSearch?.results?.totalCount) ||
    0;

  const suggestedSearchTerm = queryResult.data?.messageSearch?.suggestedSearchTerm;
  const isCorrectedWord = queryResult.data?.messageSearch?.isSpellCheckUsed;

  const headerClass =
    headerClassname ??
    cx(totalResultCount === 0 ? 'lia-messagelist-header' : 'lia-g-pt-20 lia-g-pb-15');

  /**
   * Handle the selected message sort item and update the query parameter
   *
   * @param item The selected message sort item
   */
  async function messageSortHandler(item: MessageSortMenuItem): Promise<void> {
    await router.replaceRoute<SearchResultsPageAndParams>(
      EndUserPages.SearchResultsPage,
      null,
      {
        ...router.query,
        [EndUserQueryParams.SEARCH_SORT_BY]: item
      },
      { shallow: true }
    );
  }

  /**
   * Disable spell-check and update the query parameter
   */
  async function disableSpellCheckHandler(): Promise<void> {
    await router.replaceRoute<SearchResultsPageAndParams>(
      EndUserPages.SearchResultsPage,
      null,
      {
        [EndUserQueryParams.SEARCH_KEY]: trimmedTerm,
        [EndUserQueryParams.SPELL_CHECK]: 'false'
      },
      { shallow: true }
    );
  }

  /**
   * Trigger search with the suggested search term
   */
  async function didYouMeanHandler(): Promise<void> {
    await router.replaceRoute<SearchResultsPageAndParams>(
      EndUserPages.SearchResultsPage,
      null,
      {
        [EndUserQueryParams.SEARCH_KEY]: suggestedSearchTerm
      },
      { shallow: true }
    );
  }

  /**
   * Renders individual message sort menu items in the dropdown
   * @param item
   */
  function renderMessageSortMenuItem(item: MessageSortMenuItem): React.ReactElement {
    return (
      <Dropdown.Item
        active={item === sortByValue}
        key={item}
        onSelect={async (): Promise<void> => messageSortHandler(item)}
      >
        <span aria-label={formatMessage('sort', { name: formatMessage(item) })}>
          {formatMessage(item)}
        </span>
      </Dropdown.Item>
    );
  }

  /**
   * Render the sort dropdown menu with sort options for sorting the messages
   */
  function renderSortDropdown(): React.ReactElement {
    return (
      <Dropdown>
        <Dropdown.Toggle
          as={Button}
          variant={ButtonVariant.LINK}
          size="lg"
          className={cx('lia-g-dropdown-toggle lia-sort-dropdown')}
          aria-label={formatMessage('sort', { name: formatMessage(sortByValue) })}
        >
          {formatMessage(sortByValue)}
        </Dropdown.Toggle>
        <Dropdown.Menu align="right" popperConfig={dropdownPopperConfig}>
          <Dropdown.Header>{formatMessage('sortBy')}</Dropdown.Header>
          {messageSortItems.map(sort => renderMessageSortMenuItem(sort))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  const suggestedSearchTermText = (...chunks): React.ReactNode => (
    <span className={cx('lia-spell-check-text')}>{chunks}</span>
  );

  const originalSearchTermText = (...chunks): React.ReactNode => (
    <Button
      onClick={disableSpellCheckHandler}
      variant={ButtonVariant.UNSTYLED}
      className={cx('lia-spell-check-button lia-spell-check-text')}
    >
      {chunks}
    </Button>
  );

  const didYouMeanPhrase = (...chunks): React.ReactNode => (
    <Button
      onClick={didYouMeanHandler}
      variant={ButtonVariant.UNSTYLED}
      className={cx('lia-spell-check-button lia-spell-check-text')}
    >
      {chunks}
    </Button>
  );

  /**
   * Render the spell-check header based on the corrected word
   */
  function renderSpellCheckHeader(): React.ReactElement {
    if (suggestedSearchTerm !== null && suggestedSearchTerm !== trimmedTerm) {
      return isCorrectedWord ? (
        <div className={cx('lia-g-mb-10')}>
          <p className={cx('lia-g-mb-0')}>
            <FormattedMessage
              id="suggestedSearchTerm"
              values={{
                span: suggestedSearchTermText,
                searchTerm: suggestedSearchTerm
              }}
            />
          </p>
          <p className={cx('lia-g-mb-0')}>
            <FormattedMessage
              id="originalSearchTerm"
              values={{
                button: originalSearchTermText,
                searchTerm: trimmedTerm
              }}
            />
          </p>
        </div>
      ) : (
        <p className={cx('lia-g-mb-10')}>
          <FormattedMessage
            id="didYouMeanPhrase"
            values={{
              button: didYouMeanPhrase,
              searchTerm: suggestedSearchTerm
            }}
          />
        </p>
      );
    }
    return null;
  }

  /**
   * The Header to be rendered for the PaneledItemList.
   *
   * @param key the key to fetch the header text for.
   */
  function renderResultsHeader(key: string): React.FC<React.PropsWithChildren<unknown>> {
    function Header(): React.ReactElement {
      return totalResultCount ? (
        <div>
          {renderSpellCheckHeader()}
          <div className={cx('lia-content-header')} role="status">
            <h5
              className={cx({
                'lia-content-count': totalResultCount > 0,
                'lia-content-heading': totalResultCount === 0
              })}
              aria-label={formatMessage('results', { count: totalResultCount })}
            >
              {formatMessage(key, { count: totalResultCount })}
            </h5>
            {totalResultCount > 0 && renderSortDropdown()}
          </div>
        </div>
      ) : (
        <h5 className={cx('lia-content-heading', headerClassname)}>
          {formatMessage(key, { count: 0 })}
        </h5>
      );
    }
    return Header;
  }

  if (pagerVariant) {
    pagerVariant.props = pagerVariant.props || {};
    pagerVariant.props.ariaLabel = formatMessage('showMoreMessages');
  }

  return (
    <MessageSearchContext.Provider value={searchEventTrackingIdMap}>
      <PaneledItemList<
        MessageSearchViewFragment,
        ItemType.MESSAGE_SEARCH,
        ItemViewTypeAndProps<ItemType.MESSAGE_SEARCH, MessageViewVariant>,
        MessageSearchQuery,
        MessageSearchQueryVariables
      >
        className={paneledListClassname}
        type={ItemType.MESSAGE_SEARCH}
        variant={messageViewVariant}
        queryResult={queryResult}
        itemPath="messageSearch.results"
        listVariant={listVariant}
        pagerVariant={pagerVariant}
        pageSize={loadMorePageSize || pageSize}
        loadingSize={useLoader ? LoadingSize.LG : null}
        empty={NoResults}
        header={useHeader ? renderResultsHeader('messages') : null}
        panel={panel}
        useHeaderWhenEmpty
        footerClassName={footerClassname}
        headerClassName={headerClass}
        useEmpty={useEmpty}
        onUpdate={(
          messageSearchConnection: MessageSearchConnection,
          fetchMoreResult
        ): MessageSearchQuery => {
          fetchMoreResult.messageSearch.results.edges.forEach(element => {
            searchEventTrackingIdMap[element.node.message.id] =
              fetchMoreResult.messageSearch.searchEventTrackingId;
          });

          return {
            messageSearch: {
              __typename: 'MessageSearchResults',
              results: messageSearchConnection,
              isSpellCheckUsed: isCorrectedWord,
              suggestedSearchTerm,
              searchEventTrackingId: fetchMoreResult.messageSearch.searchEventTrackingId
            }
          };
        }}
      />
    </MessageSearchContext.Provider>
  );
};

export default MessageListForSearch;
